import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import CSPFooter from "@/components/CSPFooter";

export default function Register() {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "athlete", phone: "", note: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password troppo corta (min 6)"); return; }
    setLoading(true);
    try {
      await api.post("/auth/register", { ...form, email: form.email.trim().toLowerCase() });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative bg-white items-center justify-center p-12 border-r border-zinc-200">
        <div className="relative z-10 flex flex-col items-center text-center max-w-md">
          <img src="/logo-csp.png" alt="Club Scherma Palermo" className="w-72 h-auto object-contain mb-8" />
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-tight" style={{ color: "#1a237e" }}>CSP Lab</h1>
          <p className="text-[13px] uppercase tracking-[0.2em] mt-1" style={{ color: "#b8860b" }}>Club Scherma Palermo</p>
          <p className="mt-6 text-zinc-600 text-sm">Unisciti alla piattaforma del club. Dopo l'iscrizione un amministratore approverà il tuo accesso.</p>
          <div className="mt-12 w-full"><CSPFooter variant="app" /></div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md">
          {!done && (
            <>
              <div className="mb-8">
                <h2 className="font-heading text-3xl font-bold tracking-tight">Registrati</h2>
                <p className="text-zinc-500 mt-1">Crea il tuo account. Sarà attivato dopo l'approvazione dell'amministratore.</p>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nome e cognome</Label>
                  <Input data-testid="reg-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12" required />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input data-testid="reg-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12" required />
                </div>
                <div className="space-y-2">
                  <Label>Password (min. 6 caratteri)</Label>
                  <Input data-testid="reg-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-12" minLength={6} required />
                </div>
                <div className="space-y-2">
                  <Label>Ruolo richiesto</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="reg-role" className="h-12"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="athlete">Atleta</SelectItem>
                      <SelectItem value="coach">Maestro / Allenatore</SelectItem>
                      <SelectItem value="trainer">Tecnico / Preparatore</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Telefono (opzionale)</Label>
                  <Input data-testid="reg-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12" />
                </div>
                <div className="space-y-2">
                  <Label>Note per l'admin (opzionale)</Label>
                  <Input data-testid="reg-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="h-12" placeholder="Es. sono atleta di II turno spada" />
                </div>
                <Button type="submit" data-testid="reg-submit" disabled={loading} className="w-full h-12 bg-black hover:bg-zinc-800">
                  {loading ? "Invio…" : "Invia richiesta"}
                </Button>
              </form>
              <div className="mt-6 text-center text-sm text-zinc-600">
                Hai già un account?{" "}
                <Link to="/login" data-testid="link-login" className="font-semibold text-black hover:underline">Accedi</Link>
              </div>
            </>
          )}

          {done && (
            <div data-testid="reg-success" className="text-center py-8">
              <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
              <h2 className="font-heading text-2xl font-bold tracking-tight">Richiesta inviata</h2>
              <p className="text-zinc-500 mt-2">Un amministratore riceverà la tua richiesta e attiverà l'account. Riceverai una notifica appena approvato.</p>
              <Button onClick={() => nav("/login")} data-testid="reg-back-login" className="mt-6 bg-black hover:bg-zinc-800">Torna al login</Button>
            </div>
          )}

          <div className="lg:hidden mt-10"><CSPFooter variant="app" /></div>
        </div>
      </div>
    </div>
  );
}
